import type { WithTokenParams } from '#src/app/shared/types';
import type { PaymentMethodSaveOptions, PaymentType, StripeValidationErrors } from '#src/app/shared/utils/payments';

import type { StripeElementChangeEvent } from './hooks/useStripeFormErrors';
import { useStripeFormErrors } from './hooks/useStripeFormErrors';
import { useStripeFormSubmit } from './hooks/useStripeFormSubmit';

type Params = {
	email: string;
	name: string;
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
	countryCode: string;
	currency: string;
	disabled?: boolean;
	beforeToken: (paymentType: PaymentType) => Promise<boolean>;
	onTokenError: (paymentType: PaymentType, err: Error) => void;
	onTokenInvalid: (paymentType: PaymentType, err: Error) => void;
	validate: (paymentType: PaymentType) => Promise<boolean>;
	withToken: (params: WithTokenParams) => Promise<void>;
	prePaymentChecks?: (paymentType: PaymentType) => Promise<boolean>;
};

type Result = ModelHookResult<
	{
		errorMessage?: string;
		errorType?: string;
		validationErrors: StripeValidationErrors;
	},
	{
		handleSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
		validateStripeElementOnChange: (e: StripeElementChangeEvent) => void;
		setValidationErrors: React.Dispatch<React.SetStateAction<StripeValidationErrors>>;
	}
>;

export function useStripeForm({
	beforeToken,
	email,
	name,
	paymentMethodSaveOptions,
	countryCode,
	currency,
	disabled,
	onTokenError,
	onTokenInvalid,
	validate,
	withToken,
	prePaymentChecks,
}: Params): Result {
	const {
		data,
		actions: { handleSubmitFailure, onStripeError, resetErrors, setValidationErrors, validateStripeElementOnChange },
	} = useStripeFormErrors({
		disabled,
		onTokenError,
		onTokenInvalid,
	});

	const { handleSubmit } = useStripeFormSubmit({
		beforeToken,
		email,
		name,
		paymentMethodSaveOptions,
		countryCode,
		currency,
		validate,
		withToken,
		prePaymentChecks,
		onStripeError,
		handleSubmitFailure,
		resetErrors,
		setValidationErrors,
	});

	return {
		data,
		actions: {
			handleSubmit,
			validateStripeElementOnChange,
			setValidationErrors,
		},
	};
}
